import { useState } from 'react';
import { ExternalLink, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  useFeatureStore,
  usePublishStore,
  useReviewStore,
  useSetPayoutHold,
  useSuspendStore,
  useUnsuspendStore,
  useVerifyStore,
} from '@/client/api/stores/stores.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import type { AdminStore } from '@/client/types/stores';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ReasonDialog } from '../StoreDetail/ReasonDialog';
import { storefrontPath } from './segments';

/**
 * Moderation straight from the roster, so the common calls do not need a trip
 * into the store. Same endpoints, reasons and audit trail as the Moderation tab.
 */
type QuickAction =
  | 'verify' | 'unverify' | 'feature' | 'unfeature' | 'publish' | 'unpublish'
  | 'review_hold' | 'review_clear' | 'suspend' | 'unsuspend' | 'payout_hold' | 'payout_release';

const ACTIONS: Record<QuickAction, {
  label: string; code: string; description: string; reasonRequired?: boolean; destructive?: boolean;
}> = {
  verify: { label: 'Verify', code: PERMISSIONS.store.verify, description: 'Shows the verified badge on the storefront.' },
  unverify: { label: 'Remove verification', code: PERMISSIONS.store.verify, description: 'Removes the verified badge.' },
  feature: { label: 'Feature', code: PERMISSIONS.store.feature, description: 'Gives the store featured placement.' },
  unfeature: { label: 'Unfeature', code: PERMISSIONS.store.feature, description: 'Removes featured placement.' },
  publish: { label: 'Publish', code: PERMISSIONS.store.publish, description: 'Makes the storefront publicly visible.' },
  unpublish: { label: 'Unpublish', code: PERMISSIONS.store.publish, description: 'Hides the storefront from the public.', reasonRequired: true, destructive: true },
  review_hold: { label: 'Place review hold', code: PERMISSIONS.store.review, description: 'Holds the store for staff review.', reasonRequired: true, destructive: true },
  review_clear: { label: 'Clear review hold', code: PERMISSIONS.store.review, description: 'Releases the store from review.' },
  suspend: { label: 'Suspend', code: PERMISSIONS.store.suspend, description: 'Listings go dark and payouts freeze. Orders already paid for are honoured.', reasonRequired: true, destructive: true },
  unsuspend: { label: 'Lift suspension', code: PERMISSIONS.store.suspend, description: 'Restores the store’s listings and payouts.' },
  payout_hold: { label: 'Hold payouts', code: PERMISSIONS.store.payout, description: 'Stops payouts to the seller until released.', reasonRequired: true, destructive: true },
  payout_release: { label: 'Release payout hold', code: PERMISSIONS.store.payout, description: 'Lets payouts to the seller resume.' },
};

/** The applicable half of each on/off pair for this store's current state. */
const actionsFor = (s: AdminStore): QuickAction[] => [
  s.verified ? 'unverify' : 'verify',
  s.featured ? 'unfeature' : 'feature',
  s.published ? 'unpublish' : 'publish',
  s.review ? 'review_clear' : 'review_hold',
  s.suspended ? 'unsuspend' : 'suspend',
  // Rows from an API without the rollup carry no payout state — offer neither.
  ...(s.payout_hold == null ? [] : [s.payout_hold ? 'payout_release' : 'payout_hold'] as QuickAction[]),
];

const GatedItem: React.FC<{ action: QuickAction; onSelect: () => void }> = ({ action, onSelect }) => {
  const { allowed } = useGate(ACTIONS[action].code);
  return (
    <DropdownMenuItem
      disabled={!allowed}
      onSelect={onSelect}
      className={ACTIONS[action].destructive ? 'text-red-700 focus:text-red-700' : undefined}
    >
      {ACTIONS[action].label}
    </DropdownMenuItem>
  );
};

const DEEP_LINKS = ['Orders', 'Listings', 'Payout', 'Billing', 'Moderation', 'Audit'] as const;

export type RequestStoreAction = (store: AdminStore, action: QuickAction) => void;

export const StoreRowMenu: React.FC<{ store: AdminStore; onAction: RequestStoreAction }> = ({ store, onAction }) => {
  const navigate = useNavigate();
  const path = storefrontPath(store);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for ${store.name}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      {/* React events bubble through the portal to the clickable row/card. */}
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Go to</DropdownMenuLabel>
        {DEEP_LINKS.map((tab) => (
          <DropdownMenuItem key={tab} onSelect={() => navigate(`/admin/stores/${store.storeid}?tab=${tab}`)}>
            {tab}
          </DropdownMenuItem>
        ))}
        {path && (
          <DropdownMenuItem asChild>
            <a href={`https://feelyfeely.com${path}`} target="_blank" rel="noreferrer">
              Storefront <ExternalLink className="ml-auto h-3.5 w-3.5" />
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Moderate</DropdownMenuLabel>
        {actionsFor(store).map((a) => (
          <GatedItem key={a} action={a} onSelect={() => onAction(store, a)} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** One confirm dialog for the whole roster; rows ask it to run an action. */
export const useStoreQuickActions = () => {
  const [pending, setPending] = useState<{ store: AdminStore; action: QuickAction } | null>(null);
  const suspend = useSuspendStore();
  const unsuspend = useUnsuspendStore();
  const verify = useVerifyStore();
  const feature = useFeatureStore();
  const publish = usePublishStore();
  const review = useReviewStore();
  const payoutHold = useSetPayoutHold();

  const loading = [suspend, unsuspend, verify, feature, publish, review, payoutHold].some((m) => m.isPending);
  const done = { onSuccess: () => setPending(null) };

  const run = (reason: string) => {
    if (!pending) return;
    const storeid = pending.store.storeid;
    switch (pending.action) {
      case 'verify': return verify.mutate({ storeid, verified: true, reason }, done);
      case 'unverify': return verify.mutate({ storeid, verified: false, reason }, done);
      case 'feature': return feature.mutate({ storeid, featured: true, reason }, done);
      case 'unfeature': return feature.mutate({ storeid, featured: false, reason }, done);
      case 'publish': return publish.mutate({ storeid, published: true, reason }, done);
      case 'unpublish': return publish.mutate({ storeid, published: false, reason }, done);
      case 'review_hold': return review.mutate({ storeid, review: true, review_reason: reason }, done);
      case 'review_clear': return review.mutate({ storeid, review: false, reason }, done);
      case 'suspend': return suspend.mutate({ storeid, reason }, done);
      case 'unsuspend': return unsuspend.mutate({ storeid, reason }, done);
      case 'payout_hold': return payoutHold.mutate({ storeid, hold: true, reason }, done);
      case 'payout_release': return payoutHold.mutate({ storeid, hold: false, reason }, done);
    }
  };

  const meta = pending ? ACTIONS[pending.action] : null;
  const dialog = (
    <ReasonDialog
      open={!!pending}
      title={meta && pending ? `${meta.label} — ${pending.store.name}` : ''}
      description={meta?.description}
      confirmLabel={meta?.label ?? 'Confirm'}
      destructive={meta?.destructive}
      reasonRequired={meta?.reasonRequired}
      loading={loading}
      onConfirm={run}
      onClose={() => setPending(null)}
    />
  );

  const request: RequestStoreAction = (store, action) => setPending({ store, action });
  return { request, dialog };
};
